import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { JobDto } from "@grizzly/shared";
import { jobStatus, taskPriority } from "@grizzly/shared";
import { api } from "../lib/apiClient.js";
import type { ApiClientError } from "../lib/apiClient.js";
import { formatDateTime } from "../lib/format.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, PageHeader } from "../components/ui/card.js";
import { DataTable, type Column } from "../components/ui/dataTable.js";
import { SelectInput, TextInput } from "../components/ui/field.js";
import { useI18n, type MessageKey } from "../i18n/index.js";

export function JobsPage() {
  const { t } = useI18n();
  const [items, setItems] = useState<JobDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [due, setDue] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listJobs({
        status: status || undefined,
        due: due || undefined,
        search: search || undefined,
      });
      setItems(res.items);
      setError(null);
    } catch (err) {
      setError((err as ApiClientError).message);
    } finally {
      setLoading(false);
    }
  }, [due, search, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column<JobDto>[] = [
    {
      key: "number",
      header: t("jobs.column.job"),
      render: (r) => (
        <Link
          to={`/jobs/${r.id}`}
          className="tabular font-medium text-action-primary-text hover:underline"
        >
          {r.number}
        </Link>
      ),
    },
    { key: "title", header: t("common.fields.title"), render: (r) => r.title },
    {
      key: "customer",
      header: t("common.fields.customer"),
      render: (r) => r.customerName ?? "-",
    },
    {
      key: "priority",
      header: t("common.fields.priority"),
      render: (r) => (
        <Badge tone={taskPriority(r.priority).tone}>
          {t(`status.priority.${r.priority}` as MessageKey)}
        </Badge>
      ),
    },
    {
      key: "status",
      header: t("common.fields.status"),
      render: (r) => (
        <Badge tone={jobStatus(r.status).tone}>
          {t(`status.job.${r.status}` as MessageKey)}
        </Badge>
      ),
    },
    {
      key: "due",
      header: t("jobs.column.due"),
      render: (r) => (r.dueDate ? <span className="tabular">{r.dueDate}</span> : "-"),
    },
    {
      key: "created",
      header: t("common.created"),
      render: (r) => formatDateTime(r.createdAt),
    },
  ];

  return (
    <>
      <PageHeader
        title={t("nav.jobs")}
        description={t("jobs.description")}
        actions={
          <Button variant="primary" asChild>
            <Link to="/jobs/new">{t("jobs.newJob")}</Link>
          </Button>
        }
      />

      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1">
            <TextInput
              label={t("common.fields.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={() => void load()}
              hint={t("jobs.searchHint")}
            />
          </div>
          <div className="w-48">
            <SelectInput
              label={t("common.fields.status")}
              options={[
                { value: "", label: t("jobs.filters.allStatuses") },
                { value: "draft", label: t("status.job.draft") },
                { value: "planned", label: t("status.job.planned") },
                { value: "released", label: t("status.job.released") },
                { value: "in_progress", label: t("status.job.in_progress") },
                { value: "on_hold", label: t("status.job.on_hold") },
                {
                  value: "ready_for_delivery",
                  label: t("status.job.ready_for_delivery"),
                },
                { value: "completed", label: t("status.job.completed") },
                { value: "cancelled", label: t("status.job.cancelled") },
              ]}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            />
          </div>
          <div className="w-48">
            <SelectInput
              label={t("jobs.column.due")}
              options={[
                { value: "", label: t("jobs.filters.anyDueDate") },
                { value: "overdue", label: t("jobs.filters.overdue") },
                { value: "due_soon", label: t("jobs.filters.dueSoon") },
              ]}
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {error ? (
        <p role="alert" className="mb-4 text-sm text-action-danger">
          {error}
        </p>
      ) : null}
      <DataTable
        caption={t("nav.jobs")}
        loading={loading}
        rows={items}
        rowKey={(r) => r.id}
        columns={columns}
        emptyTitle={t("jobs.emptyTitle")}
        emptyDescription={t("jobs.emptyDescription")}
      />
    </>
  );
}
